/**
 * Hooks y helpers de autenticación.
 *
 * Patrón: TanStack Query como source of truth, suscripción a auth state
 * de Supabase que invalida las queries cuando cambia.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from './supabase';
import { identify, resetAnalytics } from './analytics';
import type { Session, User } from '@supabase/supabase-js';

export const authQueryKeys = {
  session: ['auth', 'session'] as const,
  profile: (userId: string) => ['auth', 'profile', userId] as const,
};

/**
 * Hook: la sesión actual. Devuelve null si no hay sesión.
 */
export function useSession() {
  return useQuery<Session | null>({
    queryKey: authQueryKeys.session,
    queryFn: async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    },
    staleTime: Infinity, // Invalidado manualmente por onAuthStateChange
  });
}

/**
 * Hook: el usuario actual (más conveniente que session.user).
 */
export function useUser(): User | null {
  const { data: session } = useSession();
  return session?.user ?? null;
}

/**
 * Hook: setup global del listener de cambios de auth.
 * Llamar UNA SOLA VEZ en App.tsx para mantener la cache sincronizada.
 */
export function useAuthListener() {
  const queryClient = useQueryClient();
  // Última identidad conocida. Sirve para distinguir "cambió quién soy" de
  // "se renovó el token", que dispara el mismo evento muchas veces por sesión.
  const lastUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      queryClient.setQueryData(authQueryKeys.session, session);

      // Cuando aparece una identidad NUEVA (anónimo → logueado, o de una
      // cuenta a otra), todo lo cargado con la anterior queda obsoleto. Sin
      // esto, cualquier query que se resolvió mientras el visitante era anónimo
      // se sigue sirviendo desde el cache después del login: es exactamente lo
      // que dejaba al header con las iniciales en vez de la foto.
      //
      // Solo en el cambio real: no en cada refresh de token (mismo id) y no en
      // el logout (ahí no hay JWT, así que refetchear las ~15 queries montadas
      // solo produce errores que se tiran a la basura un instante después,
      // cuando AuthGuard navega a /login; ese caso ya lo cubre el `clear()` de
      // useLogout).
      const nextUserId = session?.user?.id ?? null;
      if (lastUserId.current !== undefined && nextUserId && lastUserId.current !== nextUserId) {
        queryClient.invalidateQueries();
      }
      lastUserId.current = nextUserId;

      // Si se desloguea, limpiar todo el cache para evitar leak
      if (!session) {
        queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
        resetAnalytics();
      } else if (session.user) {
        // Identify mínimo con user_id — propiedades adicionales (role,
        // plan_code, country) se enriquecen cuando se cargan en otros hooks.
        identify(session.user.id);
      }
    });
    return () => data.subscription.unsubscribe();
  }, [queryClient]);
}

/**
 * Mutación: signup con email + password.
 * Acepta captchaToken opcional (Sprint 10.54) — si Supabase tiene captcha
 * activado (config.toml en local, dashboard en prod), exige el token.
 */
export function useSignup() {
  return useMutation({
    mutationFn: async (input: {
      email: string;
      password: string;
      full_name: string;
      captchaToken?: string;
    }) => {
      const { data, error } = await supabase.auth.signUp({
        email: input.email,
        password: input.password,
        options: {
          data: { full_name: input.full_name },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          captchaToken: input.captchaToken,
        },
      });
      if (error) throw error;
      return data;
    },
  });
}

/**
 * Mutación: login con email + password.
 * Acepta captchaToken opcional (Sprint 10.54).
 */
export function useLogin() {
  return useMutation({
    mutationFn: async (input: {
      email: string;
      password: string;
      captchaToken?: string;
    }) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: input.email,
        password: input.password,
        options: {
          captchaToken: input.captchaToken,
        },
      });
      if (error) throw error;
      return data;
    },
  });
}

/**
 * Mutación: login con Google OAuth.
 * Redirige al usuario al consent screen de Google.
 */
export function useLoginWithGoogle() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      return data;
    },
  });
}

/**
 * Mutación: logout.
 *
 * Limpia sesión + cache + navega a la home pública.
 *
 * El `window.location.href = '/'` es deliberado: sin esa redirección
 * explícita, AuthGuard reacciona al cambio de sesión y redirige a
 * `/login?redirect=<página actual>`. Eso es correcto para un user
 * NO logueado que intenta entrar a una página protegida, pero en un
 * logout deliberado el redirect arrastra contexto del user anterior y
 * confunde si después te logueás con otra credencial (terminas "atrapado"
 * en una página a la que el nuevo user no tiene acceso).
 *
 * Hard navigation también garantiza que cualquier estado en memoria de la
 * SPA (cache de fetch, refs, timers) quede completamente limpio para el
 * próximo login.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
    onSettled: () => {
      queryClient.clear();
      window.location.href = '/';
    },
  });
}

/**
 * Mutación: solicitar reset de password.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
    },
  });
}
